import type { TrackInput, EventName } from './events';

import { getLogger } from '@/lib/logger/logger';

const logger = getLogger('stats');
const STATS_URL = `${import.meta.env.VITE_STATS_URL ?? ''}/track`;
const BATCH_SIZE = 10;
const BATCH_MS = 3000;

type Envelope = { session_id: string; client_ts: number };
type PayloadOf<E extends EventName> = Omit<Extract<TrackInput, { event: E }>, 'event'>;

let buffer: (TrackInput & Envelope)[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

const THROTTLE_MS = 1000;
export const lastSent = new Map<string, number>();

// ── Session ──

const SESSION_KEY = '__analytics_sid__';

function getSessionId(): string {
  try {
    const existing = localStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const id = crypto.randomUUID();
    localStorage.setItem(SESSION_KEY, id);
    return id;
  } catch {
    return crypto.randomUUID();
  }
}

// ── Internal ──

function throttleKey(event: string, payload: Record<string, unknown>): string {
  const p = (payload.product as string) ?? '';
  const f = (payload.feature as string) ?? '';
  const l = (payload.label as string) ?? '';
  return `${p}/${event}/${f}/${l}`;
}

function send(events: (TrackInput & Envelope)[]): void {
  const payload = JSON.stringify({ events });

  void fetch(STATS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: payload,
    keepalive: true,
  }).catch(err => logger.debug('stats send failed', err));
}

// ── Public API ──

interface TrackOptions {
  immediate?: boolean;
}

/**
 * 이벤트 적재 + 배치 트리거. 이벤트명에 맞는 payload만 허용. 절대 throw 하지 않는다.
 *
 * 기본은 배치(10개/3초). 누르면 곧바로 다른 페이지로 이동하는 클릭처럼
 * pagehide flush에만 의존하기 불안한 경우 `{ immediate: true }`로 즉시 전송한다.
 */
export function track<E extends EventName>(
  event: E,
  payload: PayloadOf<E>,
  opts?: TrackOptions,
): void {
  try {
    const now = Date.now();
    const key = throttleKey(event, payload);
    const prev = lastSent.get(key);
    if (prev && now - prev < THROTTLE_MS) return;
    lastSent.set(key, now);

    const entry = {
      event,
      ...payload,
      session_id: getSessionId(),
      client_ts: now,
    } as unknown as TrackInput & Envelope;
    buffer.push(entry);
    if (opts?.immediate || buffer.length >= BATCH_SIZE) {
      void flush();
      return;
    }
    timer ??= setTimeout(() => void flush(), BATCH_MS);
  } catch {
    /* 계측은 앱을 방해하지 않는다 */
  }
}

/** 버퍼 즉시 전송 (fire-and-forget). */
export function flush(): void {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (buffer.length === 0) return;
  const batch = buffer;
  buffer = [];
  send(batch);
}

const TRACKED_PRODUCTS = ['hwp', 'hcell', 'docdiff', 'ocr', 'dataloader'] as const;
type TrackedProduct = (typeof TRACKED_PRODUCTS)[number];

/** 구매/Get API 등 CTA 클릭 계측. 계측 대상 제품일 때만, 외부 이동 전 즉시 전송. */
export function trackPurchaseClick(product: string): void {
  if (!(TRACKED_PRODUCTS as readonly string[]).includes(product)) return;
  track('action', { product: product as TrackedProduct, feature: 'buyClick' }, { immediate: true });
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => void flush());
}
